import { api } from './client'
import type { SalesOrder } from './sales-order-api'

export interface CustomerGroup {
    id: string
    name: string
    description?: string
    discount_percent: number
    created_at: string
    updated_at: string
}

export interface Customer {
    id: string
    code: string
    name: string
    phone?: string
    email?: string
    address?: string
    ward?: string
    ward_code?: string
    province?: string
    province_code?: string
    tax_code?: string
    /** Tên công ty (dùng auto-fill tên người mua trên hóa đơn công ty). */
    company?: string
    customer_group_id?: string
    customer_group?: CustomerGroup
    notes?: string
    credit_limit: number
    assigned_sales_id?: string
    is_active: boolean
    // Số dư công nợ luỹ kế (denormalized từ ledger). Dương = khách nợ mình,
    // âm = khách trả trước (credit). Có thể vắng ở payload cũ chưa migrate.
    current_debt?: number
    // Chỉ danh sách trả: số ngày (lịch VN) từ khoản nợ còn mở cũ nhất.
    debt_days?: number | null
    created_at: string
    updated_at: string
}

// 1 dòng sản phẩm của đơn (rút gọn) cho cột "Sản phẩm" ở bảng phân bổ.
export interface DebtOrderItem {
    product_name: string
    quantity: number
}

export interface CustomerDebt {
    id: string
    customer_id: string
    sales_order_id?: string
    total_amount: number
    paid_amount: number
    remaining_amount: number
    status: 'unpaid' | 'partial' | 'paid'
    due_date?: string
    notes?: string
    created_at: string
    updated_at: string
    // Joined ở BE cho bảng phân bổ (payment dialog): mã đơn + danh sách SP.
    order_code?: string
    items?: DebtOrderItem[]
}

export interface CustomerCreditStatus {
    credit_limit: number
    current_debt: number
    available_credit: number
    is_unlimited: boolean
}

export interface CreateCustomerGroupRequest {
    name: string
    description?: string
    discount_percent: number
}

export interface UpdateCustomerGroupRequest {
    name: string
    description?: string
    discount_percent: number
}

export interface CreateCustomerRequest {
    name: string
    phone?: string
    email?: string
    address?: string
    ward?: string
    ward_code?: string
    province?: string
    province_code?: string
    tax_code?: string
    customer_group_id?: string
    notes?: string
    credit_limit?: number
}

export interface UpdateCustomerRequest {
    name: string
    phone?: string
    email?: string
    address?: string
    ward?: string
    ward_code?: string
    province?: string
    province_code?: string
    tax_code?: string
    customer_group_id?: string
    notes?: string
    credit_limit?: number
    is_active?: boolean
    assigned_sales_id?: string | null
}

export interface OwnedCustomerDebtSummary {
    total_debt: number
}

export interface OwnedDebtorRow {
    customer_id: string
    customer_name: string
    phone?: string
    total_owed: number
    oldest_unpaid_at: string
}

export interface ImportResult {
    imported: number
    updated: number
    skipped: number
    /** Số bút toán điều chỉnh công nợ đã ghi vào sổ. */
    debt_adjusted: number
    errors: { row: number; code: string; error: string }[]
    warnings: { row: number; phone: string; message: string }[]
}

export interface ImportCustomersParams {
    file: File
    /**
     * Ghi đè công nợ của khách ĐÃ tồn tại bằng số trong file. Không ảnh hưởng
     * khách mới tạo — khách mới luôn được ghi công nợ vì không có số dư để đè.
     */
    syncDebt: boolean
}

export interface ListCustomersParams {
    page?: number
    per_page?: number
    search?: string
    customer_group_id?: string
    is_active?: boolean
    assigned_sales_id?: string
    // Chỉ lấy khách còn nợ (current_debt > 0).
    has_debt?: boolean
    // Khoảng "Nợ hiện tại" (VND, gồm biên).
    debt_min?: number
    debt_max?: number
    sort_by?: 'current_debt' | 'name' | 'code' | 'created_at'
    sort_order?: 'asc' | 'desc'
    // with_totals='1': meta.totals.current_debt = tổng nợ của cả tập lọc.
    with_totals?: '1'
}

export interface KiotVietSyncState {
    last_status: string
    last_run_at?: string
    last_imported: number
    last_updated: number
    last_error?: string
}

export interface KiotVietSyncStatus {
    enabled: boolean
    state: KiotVietSyncState | null
}

/** `meta.totals` của danh sách khách khi gửi `with_totals=1`. */
export interface CustomerListTotals {
    current_debt: number
}

export const customerApi = {
    // Groups
    listGroups: () => api.get<CustomerGroup[]>('/customer-groups'),
    createGroup: (data: CreateCustomerGroupRequest) =>
        api.post<CustomerGroup>('/customer-groups', data),
    updateGroup: (id: string, data: UpdateCustomerGroupRequest) =>
        api.put<CustomerGroup>(`/customer-groups/${id}`, data),
    deleteGroup: (id: string) => api.delete(`/customer-groups/${id}`),

    // Customers
    list: (params?: ListCustomersParams) =>
        api.get<Customer[], CustomerListTotals>('/customers', { params }),
    getById: (id: string) => api.get<Customer>(`/customers/${id}`),
    create: (data: CreateCustomerRequest) =>
        api.post<Customer>('/customers', data),
    update: (id: string, data: UpdateCustomerRequest) =>
        api.put<Customer>(`/customers/${id}`, data),
    delete: (id: string) => api.delete(`/customers/${id}`),
    importExcel: ({ file, syncDebt }: ImportCustomersParams) => {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('sync_debt', syncDebt ? 'true' : 'false')
        return api.post<ImportResult>('/customers/import', formData)
    },

    // KiotViet sync (dormant until backend enabled)
    triggerKiotVietSync: () => api.post('/customers/kiotviet-sync'),
    getKiotVietSyncStatus: () =>
        api.get<KiotVietSyncStatus>('/customers/kiotviet-sync/status'),

    // Orders for customer
    getOrders: (id: string, params?: { page?: number; per_page?: number }) =>
        api.get<SalesOrder[]>(`/customers/${id}/orders`, { params }),

    // Debts
    listDebts: (
        id: string,
        params?: { page?: number; per_page?: number; status?: string },
    ) => api.get<CustomerDebt[]>(`/customers/${id}/debts`, { params }),

    // Credit status
    getCreditStatus: (id: string) =>
        api.get<CustomerCreditStatus>(`/customers/${id}/credit-status`),

    // Debt summary for owned customers (self-scoped to auth user)
    getDebtsSummary: () =>
        api.get<OwnedCustomerDebtSummary>('/customers/debts/summary'),

    // Per-customer debtor worklist (self-scoped to auth user)
    getDebtorList: (limit?: number) =>
        api.get<OwnedDebtorRow[]>('/customers/debts/by-customer', {
            params: limit ? { limit } : undefined,
        }),
}
